import type {
  ContentBlock,
  FunctionCall,
  AgentInfo,
  AskUserQuestion,
  Layout,
  Conversation,
  ConversationListItem,
  ChatMessageRecord,
} from '@/shared/types'
import { getAuthHeader } from '../auth'
import { API_BASE, API_URL, createHeaders } from './base'
import { fetchSseTicket } from './sse'

/**
 * Union of all SSE event shapes emitted by /api/chat/stream.
 *
 * Adding a new event type:
 *   1. Add a variant here.
 *   2. Add the corresponding case to the stream_claude_chat generator
 *      in backend/integrations/claude_sdk/chat.py.
 *   3. Handle it in useChat.ts sendMessage.
 */
export type ChatStreamEvent =
  | { type: 'text'; content: string }
  | { type: 'tool_call'; id: string; name: string; args: Record<string, unknown> }
  | { type: 'tool_result'; tool_use_id: string; content: string; is_error: boolean }
  | { type: 'subagent_text'; content: string }
  | { type: 'subagent_tool_call'; name: string; args: Record<string, unknown> }
  | { type: 'ask_user_input'; tool_use_id: string; pause_key: string; question: string; questions?: AskUserQuestion[] }
  | { type: 'done'; chat_message: string; function_calls: FunctionCall[]; content_blocks?: ContentBlock[]; agent: AgentInfo; new_layout: Layout; conversation_id: number | null; model?: string; total_cost_usd?: number | null; usage?: Record<string, number> | null; duration_ms?: number; num_turns?: number }
  | { type: 'error'; message: string }

export interface ChatHistoryMessage {
  role: string  // "user" or "model"
  content: string
}

export interface ChatAttachment {
  filename: string
  base64_data: string
  media_type: string
}

/**
 * Fire-and-forget chat trigger. Posts the message to /api/chat/stream and
 * returns the 202 envelope immediately. The actual response content arrives
 * via the global SSE event bus (source: 'chat').
 */
/** Error thrown when attempting to switch agents in a locked conversation */
export class AgentMismatchError extends Error {
  lockedAgentId: number
  requestedAgentId: number | null

  constructor(message: string, lockedAgentId: number, requestedAgentId: number | null) {
    super(message)
    this.name = 'AgentMismatchError'
    this.lockedAgentId = lockedAgentId
    this.requestedAgentId = requestedAgentId
  }
}

export async function sendChatMessage(params: {
  prompt: string
  conversationId?: number | null
  model?: string | null
  agentDefinitionId?: number | null
  attachments?: ChatAttachment[]
  workspace?: string | null
}): Promise<{ source: string; source_id: number }> {
  const response = await fetch(`${API_BASE}/chat/stream`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      prompt: params.prompt,
      history: [],
      model: params.model ?? null,
      agent_definition_id: params.agentDefinitionId ?? null,
      conversation_id: params.conversationId ?? null,
      attachments: params.attachments?.map(a => ({
        filename: a.filename,
        base64_data: a.base64_data,
        media_type: a.media_type,
      })) ?? [],
      workspace: params.workspace ?? null,
    }),
  })
  if (!response.ok) {
    // Check for agent mismatch error (400 with specific detail)
    if (response.status === 400) {
      try {
        const errorData = await response.json()
        if (errorData.detail?.error === 'agent_mismatch') {
          throw new AgentMismatchError(
            errorData.detail.message || 'Cannot switch agents in an active conversation',
            errorData.detail.locked_agent_id,
            errorData.detail.requested_agent_id
          )
        }
      } catch (e) {
        if (e instanceof AgentMismatchError) throw e
        // Fall through to generic error if parsing fails
      }
    }
    throw new Error(`Chat request failed: ${response.status}`)
  }
  return response.json()
}

export async function cancelChat(conversationId: number): Promise<void> {
  await fetch(`${API_BASE}/chat/${conversationId}/cancel`, {
    method: 'POST',
    headers: createHeaders(),
  })
}

/** Legacy single free-text reply — used by the fallback card when `questions` is absent. */
export async function respondToUserInput(conversationId: number, toolUseId: string, answer: string): Promise<void> {
  const response = await fetch(`${API_BASE}/chat/${conversationId}/respond`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ tool_use_id: toolUseId, answer }),
  })
  if (!response.ok) {
    throw new Error(`Failed to respond to user input: ${response.status}`)
  }
}

/** Multi-question reply — one answer per question, keyed by the question's exact text. */
export async function respondToUserInputAnswers(
  conversationId: number,
  pauseKey: string,
  answers: Record<string, string>,
): Promise<void> {
  const response = await fetch(`${API_BASE}/chat/${conversationId}/respond`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ pause_key: pauseKey, answers }),
  })
  if (!response.ok) {
    throw new Error(`Failed to respond to user input: ${response.status}`)
  }
}

export async function streamChatMessage(
  prompt: string,
  history: ChatHistoryMessage[],
  model: string | undefined,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
  agentDefinitionId?: number,
  conversationId?: number | null,
  attachments?: Array<{ name: string; mediaType: string; base64Data: string }>,
  workspace?: string | null,
): Promise<void> {
  const response = await fetch(`${API_BASE}/chat/stream`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      prompt,
      history,
      model,
      agent_definition_id: agentDefinitionId ?? null,
      conversation_id: conversationId ?? null,
      attachments: attachments?.map(a => ({
        filename: a.name,
        base64_data: a.base64Data,
        media_type: a.mediaType,
      })) ?? [],
      workspace: workspace ?? null,
    }),
    signal,
  })
  if (!response.ok) throw new Error('Failed to send message')
  if (!response.body) throw new Error('No response body')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            onEvent(JSON.parse(line.slice(6)) as ChatStreamEvent)
          } catch { /* skip malformed */ }
        }
      }
    }
    // Process remaining buffer (last event may be split across final chunk)
    if (buffer.trim()) {
      for (const line of buffer.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            onEvent(JSON.parse(line.slice(6)) as ChatStreamEvent)
          } catch { /* skip malformed */ }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// Conversations
export async function getInProgressConversations(): Promise<ConversationListItem[]> {
  const response = await fetch(`${API_BASE}/conversations?in_progress=true`, {
    headers: createHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch in-progress conversations')
  return response.json()
}

export async function getConversations(limit: number = 50, offset: number = 0): Promise<ConversationListItem[]> {
  const response = await fetch(`${API_BASE}/conversations?limit=${limit}&offset=${offset}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch conversations')
  return response.json()
}

export async function createConversation(title?: string, workspaceId?: number | null): Promise<Conversation> {
  const body: Record<string, unknown> = {}
  if (title) body.title = title
  if (workspaceId) body.workspace_id = workspaceId
  const response = await fetch(`${API_BASE}/conversations`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  })
  if (!response.ok) throw new Error('Failed to create conversation')
  return response.json()
}

export async function getConversation(id: number): Promise<Conversation> {
  const response = await fetch(`${API_BASE}/conversations/${id}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch conversation')
  return response.json()
}

export async function deleteConversation(id: number): Promise<void> {
  const response = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'DELETE',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete conversation')
}

export async function updateConversation(id: number, title: string): Promise<Conversation> {
  const response = await fetch(`${API_BASE}/conversations/${id}`, {
    method: 'PUT',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ title }),
  })
  if (!response.ok) throw new Error('Failed to update conversation')
  return response.json()
}

export async function addMessageToConversation(
  conversationId: number,
  role: 'user' | 'assistant',
  content: string,
  functionCalls?: FunctionCall[],
  agentInfo?: AgentInfo
): Promise<ChatMessageRecord> {
  const response = await fetch(`${API_BASE}/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      role,
      content,
      function_calls: functionCalls,
      agent_info: agentInfo
    }),
  })
  if (!response.ok) throw new Error('Failed to add message')
  return response.json()
}

// Voice transcription
const MIME_EXT: Record<string, string> = {
  'audio/webm': '.webm',
  'audio/mp4': '.mp4',
  'audio/ogg': '.ogg',
  'audio/mpeg': '.mp3',
  'audio/wav': '.wav',
}

export async function transcribeAudio(blob: Blob): Promise<{ text: string; language: string }> {
  const ext = MIME_EXT[blob.type.split(';')[0]] || '.webm'
  const form = new FormData()
  form.append('audio', blob, `recording${ext}`)
  const response = await fetch(`${API_URL}/api/v1/transcribe`, {
    method: 'POST',
    headers: { ...getAuthHeader() },
    body: form,
  })
  if (!response.ok) throw new Error(`Transcription failed: ${response.statusText}`)
  return response.json()
}

// Voice synthesis (Piper TTS)
export async function synthesizeText(text: string): Promise<ArrayBuffer> {
  const response = await fetch(`${API_URL}/api/v1/synthesize`, {
    method: 'POST',
    headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  })
  if (!response.ok) throw new Error(`Synthesis failed: ${response.statusText}`)
  return response.arrayBuffer()
}

// Thinker API
export interface ThinkerRunParams {
  goal_id?: number
  focus_area?: string
}

export type ThinkerStreamEvent =
  | { type: 'phase_start'; phase: number; name: string }
  | { type: 'message'; phase: number; text: string }
  | { type: 'tool_use'; phase: number; text: string; meta?: Record<string, unknown> }
  | { type: 'phase_complete'; phase: number }
  | { type: 'complete'; workflow_id: number; title: string }
  | { type: 'error'; message: string }

export async function getThinkerRuns(): Promise<Array<{
  id: number
  workflow_id: number
  created_at: string
  action: string
  agent: string | null
  summary: string
  details: Record<string, unknown> | null
}>> {
  const response = await fetch(`${API_URL}/thinker/runs`, {
    headers: createHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch thinker runs')
  return response.json()
}

export async function streamThinkerRun(
  params: ThinkerRunParams,
  onEvent: (event: ThinkerStreamEvent) => void,
  onError?: (error: Error) => void,
): Promise<AbortController> {
  const controller = new AbortController()

  try {
    const ticket = await fetchSseTicket()
    const url = `${API_URL}/thinker/run/stream?ticket=${encodeURIComponent(ticket)}`

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal: controller.signal,
    })

    if (!response.ok || !response.body) {
      throw new Error(`Thinker run failed: ${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            const trimmed = line.trim()
            if (!trimmed.startsWith('data:')) continue
            const raw = trimmed.slice(5).trim()
            if (!raw || raw === '[DONE]') continue

            try {
              const event = JSON.parse(raw) as ThinkerStreamEvent
              onEvent(event)
            } catch {
              // malformed SSE line — skip
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          onError?.(err instanceof Error ? err : new Error(String(err)))
        }
      }
    }

    processStream()
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)))
  }

  return controller
}
